import { useDispatch, useSelector } from 'react-redux';
import { Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { ShopAddonsLink } from '@app/router/constants';
import { useNavigate } from '@app/hooks';
import { AddOnType, openInNewTab, selectUserInfo, setCurrentShopAddonId } from '@app/redux';

import { AccountType, ProductType } from '@mfe/shared/schema-types';

import EasyCare from '../../assets/EasyCare';
import ViasatGlobe from '../../assets/ViasatGlobe';
import ViasatShield from '../../assets/ViasatShield';
import ViasatVoice from '../../assets/ViasatVoice';
import { AddOnCardLoading } from './AddOnCardLoading';

import styles from './AddOnSection.module.scss';

type AddOnCardProps = {
  type: AddOnType;
  loading?: boolean;
  offer?: ProductType;
};

const getAddOnIcon = (type: AddOnType): JSX.Element | null => {
  switch (type) {
    case AddOnType.Voice:
      return <ViasatVoice />;
    case AddOnType.EasyCare:
      return <EasyCare />;
    case AddOnType.CyberProtect:
      return <ViasatShield />;
    case AddOnType.StaticIP:
      return <ViasatGlobe />;
    default:
      return null;
  }
};

const AddOnCardIcon = ({ type }: AddOnCardProps): JSX.Element | null => {
  const icon = getAddOnIcon(type);

  if (!icon) {
    return null;
  }

  return (
    <div data-cy="add-on-card-icon" className={styles['addOnCardIcon']}>
      {icon}
    </div>
  );
};

export const AddOnCard = ({ type, loading, offer }: AddOnCardProps): JSX.Element => {
  const { t } = useTranslation(['Overview', 'Global']);
  const dispatch = useDispatch();
  const { goTo } = useNavigate();
  const {
    userInfo: { accountType },
  } = useSelector(selectUserInfo);

  const isSMBAccount = accountType === AccountType.Smb;

  const titleText = t(`Overview:Advertisement.${type}.title`);
  const captionText = t(`Overview:Advertisement.${type}.caption`);
  const learnMoreURL = t(`Overview:Advertisement.${type}.${isSMBAccount ? 'link_SMB' : 'link'}`);
  const learnMoreText = t('Global:learnMore');

  const onClick = () => {
    if (!offer) {
      dispatch(openInNewTab(learnMoreURL));
    } else if (offer.is_grouped_product) {
      dispatch(setCurrentShopAddonId(offer.id));
      goTo(`${ShopAddonsLink.ShopAddons}/${ShopAddonsLink.GroupedAddon}`);
    } else {
      dispatch(setCurrentShopAddonId(offer.id));
      goTo(`${ShopAddonsLink.ShopAddons}/${ShopAddonsLink.SingleAddon}`);
    }
  };

  return (
    <div data-cy="add-on-card" className={styles['addOnCardLayout']}>
      <div className={styles['addOnCardContent']}>
        {loading ? (
          <AddOnCardLoading />
        ) : (
          <>
            <div className={styles['addOnCardTitle']}>
              <AddOnCardIcon type={type} />
              <div data-cy="title-text">
                <Txt variant="bodyLargeBold">{titleText}</Txt>
              </div>
            </div>
            <div data-cy="caption-text" className={styles['addOnCardCaption']}>
              <Txt variant="bodyLargeRegular">{captionText}</Txt>
            </div>
            <div className={styles['addOnCardLink']}>
              <a data-cy="add-on-card-link" onClick={onClick}>
                <Txt variant="labelSmall">{learnMoreText}</Txt>
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
